/** @forge-header
 * @id          activityContext
 * @role        Activity event feed context | ring buffer of 200 order/connection/error events
 * @pattern     context-provider
 * @tier        T1
 * @purity      none
 * @units       n/a
 * @upstream    none (leaf provider)
 * @downstream  ActivityRail | AppHeader | any component via useActivity()
 * @exports     ActivityProvider:component | useActivity:fn
 *              | ActivityEvent:interface | ActivityEventType:type
 * @test        untested
 */

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

export type ActivityEventType =
  | 'order_placed'
  | 'order_filled'
  | 'order_cancelled'
  | 'queue_velocity'
  | 'liquidity_sweep'
  | 'connection_change'
  | 'connection_lost'
  | 'connection_restored'
  | 'error';

export interface ActivityEvent {
  id: string;
  type: ActivityEventType;
  message: string;
  /** Dollar amount if applicable */
  amount?: number;
  /** Related order ID for click-to-navigate */
  orderId?: string;
  /** Related market ticker */
  ticker?: string;
  timestamp: number;
}

const MAX_EVENTS = 1000;
const STORAGE_KEY = 'g1xl-activity-log-v1';

function hydrateFromStorage(): ActivityEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_EVENTS) as ActivityEvent[];
  } catch {
    return [];
  }
}

function persistToStorage(events: ActivityEvent[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    // localStorage full or unavailable
  }
}

interface ActivityContextValue {
  events: ActivityEvent[];
  push: (event: Omit<ActivityEvent, 'id' | 'timestamp'>) => void;
  clear: () => void;
}

const ActivityContext = createContext<ActivityContextValue | null>(null);

let nextId = 0;

export function ActivityProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<ActivityEvent[]>(hydrateFromStorage);
  const eventsRef = useRef<ActivityEvent[]>(events);

  const push = useCallback((event: Omit<ActivityEvent, 'id' | 'timestamp'>) => {
    const full: ActivityEvent = {
      ...event,
      id: `act-${++nextId}`,
      timestamp: Date.now(),
    };

    eventsRef.current = [full, ...eventsRef.current].slice(0, MAX_EVENTS);
    setEvents(eventsRef.current);
    persistToStorage(eventsRef.current);
  }, []);

  const clear = useCallback(() => {
    eventsRef.current = [];
    setEvents([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // noop
    }
  }, []);

  return (
    <ActivityContext.Provider value={{ events, push, clear }}>{children}</ActivityContext.Provider>
  );
}

export function useActivity(): ActivityContextValue {
  const ctx = useContext(ActivityContext);
  if (!ctx) {
    throw new Error('useActivity must be used within <ActivityProvider>');
  }
  return ctx;
}
